import React, { useEffect, useState, useCallback, useRef } from "react";
import { useIntl, FormattedDate } from "react-intl";
import { useHistory } from "react-router-dom";
import { Map, User, FileText, X, Square, CheckSquare } from "react-feather";
import Skeleton from "../../components/Skeleton";
import Pagination from "../../components/Pagination";
import FeedbackMessage from "../../components/FeedbackMessage";
import { api, getAnalysisHistory, processAnalysis } from "../../api";
import { currencyFormat } from "../../utils/format";
import { useAmbient } from '../../hooks/ambient';
import Multiselect from '../../components/Multiselect';
import "./styles.scss";

const PAGE_SIZE = 20;

const History = ({ setError }) => {
  const history = useHistory();
  const { formatMessage, formatNumber } = useIntl();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [activeReport, setActiveReport] = useState(null);
  const [selectedReports, setSelectedReports] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [filterOptions, setFilterOptions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const analysisErrorRef = useRef(null);

  const { isEnterpriseEnv, ambient } = useAmbient();

  useEffect(() => {
    if (isEnterpriseEnv && ambient.admin)
      api.get('/b3_consultas/get_users_list_enterprise')
        .then(({ data: response }) => {
          if (response.admin) {
            const users = response.result
              .map(r =>  ({ value: r.pessoa_id, label: r.nome }));
            setFilterOptions(users);
          } else {
            setError(formatMessage({ id: "erro-api" }));
          }
        })
        .catch(() => setError(formatMessage({ id: "erro-api" })))
  }, [setError, formatMessage, isEnterpriseEnv, ambient])

  const handleRowClick = report => {
    if (report.processado) {
      history.push(`/results/${report.id}`);
    } else {
      setActiveReport(report);
    }
  };

  const handleSelectAll = e => {
    const unprocessedReports = reports.filter(
      report => !report.B3Consulta.processado
    );

    if (e.target.checked) {
      setSelectedReports(
        unprocessedReports.map(report => report.B3Consulta.id)
      );
    } else {
      setSelectedReports([]);
    }
  };

  const handleReportSelect = (e, reportId) => {
    if (e.target.checked) {
      setSelectedReports(reports => [...reports, reportId]);
    } else {
      setSelectedReports(reports => reports.filter(rid => rid !== reportId));
    }
  };

  const handleExecuteAnalysis = async reportId => {
    setLoading(true);

    Promise.allSettled(
      (reportId ? [reportId] : selectedReports)
        .map(id => processAnalysis(id, { isEnterpriseEnv }))
    ).then(responses => {
      const errorMessages = responses
        .filter(res => res.status === "rejected")
        .map(res => res.reason.response.data.message);

      if (responses.length > errorMessages.length) {
        setSuccessMessage(
          formatMessage(
            { id: "executadas-sucesso" },
            { num: responses.length - errorMessages.length }
          )
        );
      }

      if (errorMessages.length > 0) {
        setAnalysisError(
          errorMessages.reduce(
            (prev, cur) => `${prev}\n· ${cur}`,
            formatMessage(
              { id: "executadas-erro" },
              { num: errorMessages.length }
            )
          )
        );
      }

      if (isEnterpriseEnv && ambient.admin) {
        const historyOptions = {};
  
        if (selectedFilters.length > 0) {
          historyOptions['conditions'] = {
            "B3Consulta.pessoa_id": selectedFilters.map(filter => filter.value)
          };
        }

        fetchHistory({ isEnterpriseEnv, conditions: historyOptions.conditions });
      } else {
        fetchHistory({ isEnterpriseEnv });
      }

      activeReport && setActiveReport(null);
      selectedReports.length > 0 && setSelectedReports([]);
    });
  };

  const allSelected = useCallback(
    () =>
      reports.length > 0 &&
      reports.filter(report => !report.B3Consulta.processado).length ===
        selectedReports.length,
    [selectedReports, reports]
  );

  const fetchHistory = useCallback((options = {}) => {
    setLoading(true);

    if (Object.keys(options).length === 0)
      options.isEnterpriseEnv = isEnterpriseEnv

    getAnalysisHistory(page, PAGE_SIZE, options)
      .then(res => {
        setReports(res.data.data);
        setTotalPages(res.data.totalPages);
      })
      .catch(() => setError(formatMessage({ id: "erro-api" })))
      .finally(() => {
        setLoading(false);
      });
  }, [setError, formatMessage, page, isEnterpriseEnv]);

  useEffect(() => {
    fetchHistory({ isEnterpriseEnv });
  }, [fetchHistory, isEnterpriseEnv]);

  const handleChangeOnFilter = useCallback(filters => {
    if (isEnterpriseEnv && ambient.admin) {
      const historyOptions = {};

      if (filters.length > 0) {
        historyOptions['conditions'] = {
          "B3Consulta.pessoa_id": filters.map(filter => filter.value)
        };
      }

      fetchHistory({ isEnterpriseEnv, conditions: historyOptions.conditions })
    }
  }, [isEnterpriseEnv, ambient, fetchHistory]);

  useEffect(() => {
    handleChangeOnFilter(selectedFilters);
  }, [selectedFilters, handleChangeOnFilter]);

  return (
    <>
      <h1>{formatMessage({ id: "historico" })}</h1>

      <div className="content-box history">
        {isEnterpriseEnv && ambient.admin && reports.length !== 0 && (
          <div className="history-filter">
            <span>{formatMessage({ id: 'filtrar-por-usuario' })}</span>
            <Multiselect 
              selectAll
              options={filterOptions}
              onChange={(e) => setSelectedFilters(e)}
            />
          </div>
        )}

        {!loading && reports.length === 0 ? (
          <div className="no-results">
            <FileText size="48px" />
            <p>{formatMessage({ id: "nenhuma-consulta-encontrada" })}</p>
          </div>
        ) : (
          <>
            {selectedReports.length > 0 && (
              <button
                className={`button is-small secondary${
                  loading ? " is-loading" : ""
                }`}
                onClick={() => handleExecuteAnalysis()}
              >
                {formatMessage({ id: "executar-selecionadas" })}
              </button>
            )}
            <table className="table is-striped is-hoverable">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      title={formatMessage({ id: "selecionar-todos" })}
                      onChange={handleSelectAll}
                      checked={allSelected()}
                    />
                  </th>
                  <th>{formatMessage({ id: "data-consulta" })}</th>
                  {isEnterpriseEnv && 
                    <th>{formatMessage({ id: "solicitante-consulta" })}</th>}
                    
                  <th>{formatMessage({ id: "nome-consulta" })}</th>
                  <th>{formatMessage({ id: "processada" })}</th>
                  <th>{formatMessage({ id: "tipos-criterio" })}</th>
                </tr>
              </thead>
              <tbody>
                {loading &&
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(5)].map((_, j) => (
                        <td key={j}>
                          <Skeleton />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!loading &&
                  reports.length > 0 &&
                  reports.map(rep => (
                    <tr
                      key={rep.B3Consulta.id}
                      data-testid={`result-${rep.B3Consulta.id}`}
                      onClick={() => handleRowClick(rep.B3Consulta)}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedReports.includes(rep.B3Consulta.id)}
                          onClick={e => e.stopPropagation()}
                          onChange={e =>
                            handleReportSelect(e, rep.B3Consulta.id)
                          }
                          disabled={rep.B3Consulta.processado}
                        />
                      </td>
                      <td>
                        <FormattedDate
                          value={new Date(rep.B3Consulta.data_consulta)}
                          year="numeric"
                          month="numeric"
                          day="numeric"
                          hour="numeric"
                          minute="numeric"
                        />
                      </td>
                      {isEnterpriseEnv && (
                        <td>
                          {rep.Pessoa.PessoaFisica.nome || ''}
                        </td>
                      )}
                      <td>
                        {rep.B3Consulta.nome || (
                          <span className="untitled">
                            {formatMessage({ id: "sem-titulo" })}
                          </span>
                        )}
                      </td>
                      <td>
                        {rep.B3Consulta.processado ? (
                          <CheckSquare
                            title={formatMessage({
                              id: "processada"
                            })}
                          />
                        ) : (
                          <Square
                            title={formatMessage({
                              id: "nao-processada"
                            })}
                          />
                        )}
                      </td>
                      <td>
                        {rep.B3Consulta.requisicao_json.cpf_cnpj &&
                          rep.B3Consulta.requisicao_json.cpf_cnpj.length >
                            0 && (
                            <User
                              size="20px"
                              title={formatMessage({
                                id: "possui-criterio-documento"
                              })}
                            />
                          )}{" "}
                        {rep.B3Consulta.requisicao_json.area_id &&
                          rep.B3Consulta.requisicao_json.area_id.length > 0 && (
                            <Map
                              size="20px"
                              title={formatMessage({
                                id: "possui-criterio-area"
                              })}
                            />
                          )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            <Pagination page={page} totalPages={totalPages} setPage={setPage} />
          </>
        )}

        {activeReport && (
          <div className="modal is-active">
            <div
              className="modal-background"
              onClick={() => setActiveReport(null)}
            ></div>
            <div className="modal-card">
              <header className="modal-card-head">
                <div className="modal-card-title">
                  {formatMessage({ id: "executar-analise" })}
                </div>
                <X
                  data-testid="close-button"
                  className="close-button"
                  onClick={() => setActiveReport(null)}
                />
              </header>
              <section className="modal-card-body">
                <p>
                  <span className="bold">
                    {formatMessage({ id: "nome-consulta" })}:
                  </span>{" "}
                  {activeReport.nome || (
                    <span className="untitled">
                      {formatMessage({ id: "sem-titulo" })}
                    </span>
                  )}
                </p>
                <p>
                  <span className="bold">
                    {formatMessage({ id: "data-consulta" })}:
                  </span>{" "}
                  <FormattedDate value={new Date(activeReport.data_consulta)} />
                </p>
                {!isEnterpriseEnv && (
                  <p>
                    <span className="bold">
                      {formatMessage({ id: "valor" })}:
                    </span>{" "}
                    {formatNumber(activeReport.preco, currencyFormat)}
                  </p>
                )}
              </section>
              <footer className="modal-card-foot">
                <button
                  className="button"
                  onClick={() => setActiveReport(null)}
                >
                  {formatMessage({ id: "cancelar" })}
                </button>
                <button
                  className={`button primary${loading ? " is-loading" : ""}`}
                  onClick={() => handleExecuteAnalysis(activeReport.id)}
                >
                  {formatMessage({ id: "Executar" })}
                </button>
              </footer>
            </div>
          </div>
        )}
      </div>

      {analysisError && (
        <FeedbackMessage
          ref={analysisErrorRef}
          level="error"
          message={analysisError}
          onDismiss={() => setAnalysisError(null)}
        />
      )}
      {successMessage && (
        <FeedbackMessage
          style={{
            top: analysisErrorRef.current
              ? `${analysisErrorRef.current.clientHeight + 24}px`
              : undefined
          }}
          level="success"
          message={successMessage}
          onDismiss={() => setSuccessMessage(null)}
        />
      )}
    </>
  );
};

export default History;
